import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import {
    Users, GitMerge, RefreshCw, CheckCircle, XCircle,
    Play, Wallet, TrendingUp, Search,
    Edit, X, AlertTriangle, Clock, Timer, Zap,
    ArrowRight, Shield, Star
} from 'lucide-react';
import API_BASE_URL from '../../config/api';

const AdminBinaryPayouts = () => {
    const [loading, setLoading] = useState(true);
    const [payouts, setPayouts] = useState([]);
    const [stats, setStats] = useState({});
    const [processing, setProcessing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const filterEligible = 'all';
    const [updateModal, setUpdateModal] = useState(null);
    const [message, setMessage] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null);

    const [activeTab, setActiveTab] = useState('all'); // 'all', 'eligible', 'getting_payout'

    const fetchPayouts = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/binary_payouts.php?action=list`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setPayouts(data.data.payouts || []);
                setStats(data.data.stats || {});
            } else {
                console.error(data.message);
            }
        } catch (err) {
            console.error('Failed to fetch payout data', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPayouts();
    }, [fetchPayouts]);

    // Cycle timer countdown
    const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
    useEffect(() => {
        const timer = setInterval(() => {
            const now = new Date();
            const currentHour = now.getHours();
            let cycleEnd;
            if (currentHour < 12) {
                cycleEnd = new Date(now);
                cycleEnd.setHours(12, 0, 0, 0);
            } else {
                cycleEnd = new Date(now);
                cycleEnd.setDate(cycleEnd.getDate() + 1);
                cycleEnd.setHours(0, 0, 0, 0);
            }
            const diff = Math.max(0, cycleEnd.getTime() - now.getTime());
            const hours = Math.floor(diff / 3600000);
            const minutes = Math.floor((diff % 3600000) / 60000);
            const seconds = Math.floor((diff % 60000) / 1000);
            setCountdown({ hours, minutes, seconds });
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Determine current cycle info
    const getCurrentCycleDetails = () => {
        const now = new Date();
        const currentHour = now.getHours();
        if (currentHour < 12) {
            return {
                label: '12:00 AM → 12:00 PM',
                payoutTime: '12:05 PM',
                cycleType: 'morning',
                color: 'amber',
                gradient: 'from-amber-500 to-orange-600',
                icon: '🌅'
            };
        } else {
            return {
                label: '12:00 PM → 12:00 AM',
                payoutTime: '12:05 AM',
                cycleType: 'evening',
                color: 'indigo',
                gradient: 'from-indigo-500 to-purple-600',
                icon: '🌙'
            };
        }
    };
    const currentCycle = getCurrentCycleDetails();

    // Show confirmation modal for processing all payouts
    const showProcessConfirmation = () => {
        setConfirmModal({
            title: 'Process All Payouts',
            message: 'Process payouts for all users? This will calculate matching PV and update carry forwards.',
            onConfirm: executeProcessAllPayouts,
            confirmText: 'Process All',
            confirmColor: 'indigo'
        });
    };

    // Execute the actual payout processing
    const executeProcessAllPayouts = async () => {
        setConfirmModal(null);
        try {
            setProcessing(true);
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/binary_payouts.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'process_all' })
            });
            const data = await response.json();

            if (data.success) {
                setMessage({ type: 'success', text: data.message });
                fetchPayouts();
            } else {
                setMessage({ type: 'error', text: data.message });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to process payouts' });
        } finally {
            setProcessing(false);
        }
    };

    const processSingleUser = async (userId) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/binary_payouts.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'process_user', user_id: userId })
            });
            const data = await response.json();

            if (data.success) {
                setMessage({ type: 'success', text: `Processed payout for user ${userId}` });
                fetchPayouts();
            } else {
                setMessage({ type: 'error', text: data.message });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to process user payout' });
        }
    };

    const sendEligibilityBonus = async (userId) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/binary_payouts.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'send_eligibility_bonus', user_id: userId })
            });
            const data = await response.json();

            if (data.success) {
                setMessage({ type: 'success', text: data.message });
                fetchPayouts();
            } else {
                setMessage({ type: 'error', text: data.message });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to send eligibility bonus' });
        }
    };

    const handleUpdateValue = async (userId, leftPV, rightPV, leftCF, rightCF) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/binary_payouts.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'set_test_pv',
                    user_id: userId,
                    left_pv: leftPV,
                    right_pv: rightPV,
                    left_carry_forward_pv: leftCF,
                    right_carry_forward_pv: rightCF
                })
            });
            const data = await response.json();

            if (data.success) {
                setMessage({ type: 'success', text: data.message });
                setUpdateModal(null);
                fetchPayouts();
            } else {
                setMessage({ type: 'error', text: data.message });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to update PV/CF values' });
        }
    };

    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    const formatPV = (pv) => parseFloat(pv || 0).toFixed(2);

    // Derived lists
    const eligibleUsers = payouts.filter(p => p.is_eligible);
    const gettingPayoutUsers = payouts.filter(p => p.is_eligible && p.matching_income > 0);
    const payoutRequestUsers = payouts.filter(p => p.payout_status === 'new');

    // Tab-based filtering
    const getTabPayouts = () => {
        switch (activeTab) {
            case 'eligible':
                return eligibleUsers;
            case 'getting_payout':
                return gettingPayoutUsers;
            case 'requests':
                return payoutRequestUsers;
            default:
                return payouts;
        }
    };

    const filteredPayouts = getTabPayouts().filter(p => {
        const matchesSearch = (p.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.referral_code || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterEligible === 'all' ||
            (filterEligible === 'new' && p.payout_status === 'new') ||
            (filterEligible === 'complete' && p.payout_status === 'complete') ||
            (filterEligible === 'pending' && p.payout_status === 'pending');
        return matchesSearch && matchesFilter;
    });

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="flex flex-col items-center gap-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                        <p className="text-gray-400 text-sm font-medium animate-pulse">Loading payout data...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Binary Team Payouts</h1>
                        <p className="text-gray-500 text-sm mt-1">Manage matching PV, carry forward and process payouts</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={fetchPayouts}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
                        >
                            <RefreshCw size={18} />
                            Refresh
                        </button>
                        <button
                            onClick={showProcessConfirmation}
                            disabled={processing}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50"
                        >
                            {processing ? (
                                <RefreshCw className="animate-spin" size={18} />
                            ) : (
                                <Play size={18} />
                            )}
                            Process All Payouts
                        </button>
                    </div>
                </div>

                {/* Message */}
                {message && (
                    <div className={`p-4 rounded-xl flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                        {message.type === 'success' ? <CheckCircle size={20} /> : <XCircle size={20} />}
                        {message.text}
                        <button onClick={() => setMessage(null)} className="ml-auto">×</button>
                    </div>
                )}

                {/* ═══════════════ CYCLE STATUS & TIMER CARD ═══════════════ */}
                <div className={`bg-gradient-to-br ${currentCycle.gradient} rounded-2xl p-6 shadow-xl text-white relative overflow-hidden`}>
                    <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>

                    <div className="relative z-10">
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                            {/* Cycle Info */}
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                                        <Timer size={22} />
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-widest text-white/70">Current Cycle</p>
                                        <h2 className="text-xl font-black tracking-tight">{currentCycle.icon} {currentCycle.label}</h2>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 rounded-lg backdrop-blur-sm">
                                        <Zap size={14} className="text-yellow-300" />
                                        <span className="text-sm font-bold">Payout at <span className="text-yellow-300">{currentCycle.payoutTime}</span></span>
                                    </div>
                                </div>
                            </div>

                            {/* Countdown Timer */}
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                    {[
                                        { value: String(countdown.hours).padStart(2, '0'), label: 'HRS' },
                                        { value: String(countdown.minutes).padStart(2, '0'), label: 'MIN' },
                                        { value: String(countdown.seconds).padStart(2, '0'), label: 'SEC' },
                                    ].map((unit, i) => (
                                        <React.Fragment key={i}>
                                            {i > 0 && <span className="text-2xl font-black text-white/50 animate-pulse">:</span>}
                                            <div className="bg-white/20 backdrop-blur-md rounded-xl px-4 py-3 text-center min-w-[72px]">
                                                <p className="text-2xl font-black tracking-tight tabular-nums">{unit.value}</p>
                                                <p className="text-[9px] font-bold uppercase tracking-widest text-white/60 mt-0.5">{unit.label}</p>
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Cycle Payout Schedule */}
                        <div className="mt-5 pt-5 border-t border-white/15">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-3 backdrop-blur-sm">
                                    <div className="w-10 h-10 bg-amber-500/30 rounded-lg flex items-center justify-center text-lg">🌅</div>
                                    <div>
                                        <p className="text-xs font-bold text-white/60 uppercase tracking-wider">Morning Cycle</p>
                                        <p className="font-bold">12:00 AM → 12:00 PM <ArrowRight size={14} className="inline text-yellow-300 mx-1" /> <span className="text-yellow-300">Payout @ 12:05 PM</span></p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-3 backdrop-blur-sm">
                                    <div className="w-10 h-10 bg-indigo-500/30 rounded-lg flex items-center justify-center text-lg">🌙</div>
                                    <div>
                                        <p className="text-xs font-bold text-white/60 uppercase tracking-wider">Evening Cycle</p>
                                        <p className="font-bold">12:00 PM → 12:00 AM <ArrowRight size={14} className="inline text-yellow-300 mx-1" /> <span className="text-yellow-300">Payout @ 12:05 AM</span></p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ═══════════════ QUICK STATS ═══════════════ */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <Users className="text-blue-600" size={20} />
                            </div>
                            <span className="text-gray-500 text-sm">Total Users</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{stats.total_users || 0}</p>
                    </div>

                    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <CheckCircle className="text-green-600" size={20} />
                            </div>
                            <span className="text-gray-500 text-sm">Eligible</span>
                        </div>
                        <p className="text-2xl font-bold text-green-600">{eligibleUsers.length}</p>
                    </div>

                    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <Zap className="text-purple-600" size={20} />
                            </div>
                            <span className="text-gray-500 text-sm">Getting Payout</span>
                        </div>
                        <p className="text-2xl font-bold text-purple-600">{gettingPayoutUsers.length}</p>
                    </div>

                    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-indigo-100 rounded-lg">
                                <GitMerge className="text-indigo-600" size={20} />
                            </div>
                            <span className="text-gray-500 text-sm">Cycle Payout</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_current_payout)}</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-5 shadow-lg text-white col-span-2 md:col-span-1">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-white/20 rounded-lg">
                                <Wallet size={20} />
                            </div>
                            <span className="text-white/80 text-sm">All-Time Payout</span>
                        </div>
                        <p className="text-2xl font-bold">{formatCurrency(stats.total_all_time_payout)}</p>
                    </div>
                </div>

                {/* Total Carry Forward */}
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <TrendingUp className="text-purple-600" size={20} />
                        Total Carry Forward Across All Users
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-xl p-4 text-center">
                            <p className="text-sm text-blue-600 mb-1">Left Carry Forward</p>
                            <p className="text-xl font-bold text-blue-700">{formatPV(stats.total_left_carry_forward)} PV</p>
                        </div>
                        <div className="bg-purple-50 rounded-xl p-4 text-center">
                            <p className="text-sm text-purple-600 mb-1">Right Carry Forward</p>
                            <p className="text-xl font-bold text-purple-700">{formatPV(stats.total_right_carry_forward)} PV</p>
                        </div>
                    </div>
                </div>

                {/* ═══════════════ PAYOUT REQUEST / ELIGIBLE / GETTING PAYOUT CARDS ═══════════════ */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Payout Requests Card */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-white">
                                <Clock size={18} />
                                <h3 className="font-bold text-sm">Payout Requests</h3>
                            </div>
                            <span className="bg-white/20 text-white px-2.5 py-1 rounded-full text-xs font-black">
                                {payoutRequestUsers.length}
                            </span>
                        </div>
                        <div className="max-h-[300px] overflow-y-auto">
                            {payoutRequestUsers.length === 0 ? (
                                <div className="p-6 text-center text-gray-400 text-sm">
                                    No payout requests this cycle
                                </div>
                            ) : (
                                payoutRequestUsers.map((u, i) => (
                                    <div key={i} className="px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-blue-50/30 transition-colors">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-xs">
                                                    {(u.user_name || 'U').charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-gray-900 text-sm">{u.user_name}</p>
                                                    <p className="text-[10px] text-gray-400 font-mono uppercase">{u.referral_code}</p>
                                                </div>
                                            </div>
                                            <span className="text-sm font-bold text-green-600">{formatCurrency(u.matching_income)}</span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Eligible Users Card */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-5 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-white">
                                <Shield size={18} />
                                <h3 className="font-bold text-sm">Eligible Users</h3>
                            </div>
                            <span className="bg-white/20 text-white px-2.5 py-1 rounded-full text-xs font-black">
                                {eligibleUsers.length}
                            </span>
                        </div>
                        <div className="max-h-[300px] overflow-y-auto">
                            {eligibleUsers.length === 0 ? (
                                <div className="p-6 text-center text-gray-400 text-sm">
                                    No eligible users found
                                </div>
                            ) : (
                                eligibleUsers.map((u, i) => (
                                    <div key={i} className="px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-green-50/30 transition-colors">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-700 font-bold text-xs">
                                                    {(u.user_name || 'U').charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-gray-900 text-sm">{u.user_name}</p>
                                                    <p className="text-[10px] text-gray-400 font-mono uppercase">{u.referral_code}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xs text-gray-400">L: {formatPV(u.eligibility_details?.left_direct_pv)} | R: {formatPV(u.eligibility_details?.right_direct_pv)}</p>
                                                <p className="text-[10px] text-gray-300">Direct: {u.eligibility_details?.left_count || 0}L / {u.eligibility_details?.right_count || 0}R</p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Getting Binary Payout Card */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="bg-gradient-to-r from-purple-600 to-violet-600 px-5 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-white">
                                <Star size={18} />
                                <h3 className="font-bold text-sm">Getting Binary Payout</h3>
                            </div>
                            <span className="bg-white/20 text-white px-2.5 py-1 rounded-full text-xs font-black">
                                {gettingPayoutUsers.length}
                            </span>
                        </div>
                        <div className="max-h-[300px] overflow-y-auto">
                            {gettingPayoutUsers.length === 0 ? (
                                <div className="p-6 text-center text-gray-400 text-sm">
                                    No users getting payout this cycle
                                </div>
                            ) : (
                                gettingPayoutUsers.map((u, i) => (
                                    <div key={i} className="px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-purple-50/30 transition-colors">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 font-bold text-xs">
                                                    {(u.user_name || 'U').charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-gray-900 text-sm">{u.user_name}</p>
                                                    <p className="text-[10px] text-gray-400 font-mono uppercase">{u.referral_code}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-black text-green-600">{formatCurrency(u.matching_income)}</p>
                                                <p className="text-[10px] text-gray-400">{formatPV(u.matching_pv)} PV matched</p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* ═══════════════ TAB FILTERS ═══════════════ */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                    <div className="flex flex-col md:flex-row gap-4">
                        {/* Tabs */}
                        <div className="flex gap-2 flex-wrap">
                            {[
                                { key: 'all', label: 'All Users', count: payouts.length, color: 'gray' },
                                { key: 'requests', label: 'Payout Requests', count: payoutRequestUsers.length, color: 'blue' },
                                { key: 'eligible', label: 'Eligible', count: eligibleUsers.length, color: 'green' },
                                { key: 'getting_payout', label: 'Getting Payout', count: gettingPayoutUsers.length, color: 'purple' },
                            ].map(tab => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${activeTab === tab.key
                                        ? `bg-${tab.color}-600 text-white shadow-lg shadow-${tab.color}-200`
                                        : `bg-${tab.color}-50 text-${tab.color}-700 hover:bg-${tab.color}-100`
                                        }`}
                                    style={activeTab === tab.key ? {
                                        backgroundColor: tab.color === 'gray' ? '#4b5563' : tab.color === 'blue' ? '#2563eb' : tab.color === 'green' ? '#16a34a' : '#9333ea',
                                        color: 'white'
                                    } : {
                                        backgroundColor: tab.color === 'gray' ? '#f9fafb' : tab.color === 'blue' ? '#eff6ff' : tab.color === 'green' ? '#f0fdf4' : '#faf5ff',
                                        color: tab.color === 'gray' ? '#374151' : tab.color === 'blue' ? '#1d4ed8' : tab.color === 'green' ? '#15803d' : '#7e22ce'
                                    }}
                                >
                                    {tab.label}
                                    <span className={`px-1.5 py-0.5 rounded-md text-xs font-black ${activeTab === tab.key ? 'bg-white/20' : 'bg-black/5'}`}>
                                        {tab.count}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* Search */}
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search by name or User ID..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* ═══════════════ USERS TABLE / MOBILE CARDS ═══════════════ */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {/* Desktop Table View */}
                    <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full text-left text-sm border-collapse">
                            <thead className="bg-gray-50 text-gray-500 text-[10px] font-black uppercase tracking-widest border-b border-gray-100">
                                <tr>
                                    <th className="px-6 py-4">Growth Partner</th>
                                    <th className="px-6 py-4">Lifecycle</th>
                                    <th className="px-6 py-4">Growth L/R</th>
                                    <th className="px-6 py-4">Effective L/R</th>
                                    <th className="px-6 py-4">Carry Fwd L/R</th>
                                    <th className="px-6 py-4 text-center">Match PV</th>
                                    <th className="px-6 py-4">Income</th>
                                    <th className="px-6 py-4 text-center">Bonus</th>
                                    <th className="px-6 py-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filteredPayouts.map((p, idx) => (
                                    <tr key={idx} className={`hover:bg-indigo-50/20 transition-colors ${p.is_eligible && p.matching_income > 0 ? 'bg-green-50/20' : ''}`}>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black shadow-sm ${p.is_eligible && p.matching_income > 0
                                                    ? 'bg-green-100 text-green-700'
                                                    : p.is_eligible
                                                        ? 'bg-blue-100 text-blue-700'
                                                        : 'bg-gray-100 text-gray-600'
                                                    }`}>
                                                    {(p.user_name || 'U').charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="font-black text-gray-900 leading-none mb-1">{p.user_name || 'N/A'}</p>
                                                    <p className="text-[10px] font-bold text-gray-400 font-mono italic">ID: {p.referral_code}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1">
                                                {p.payout_status === 'new' ? (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-blue-50 text-blue-700 rounded-lg text-[10px] font-black uppercase tracking-wider border border-blue-100">
                                                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></span>
                                                        New Cycle
                                                    </span>
                                                ) : p.payout_status === 'complete' ? (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-green-50 text-green-700 rounded-lg text-[10px] font-black uppercase tracking-wider border border-green-100">
                                                        <CheckCircle size={10} />
                                                        Processed
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-gray-50 text-gray-500 rounded-lg text-[10px] font-black uppercase tracking-wider border border-gray-100">
                                                        Inactive
                                                    </span>
                                                )}
                                                {p.is_eligible && (
                                                    <span className="text-[9px] font-black text-indigo-500 uppercase italic">✓ Eligible Partner</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <div className="flex gap-2 items-center">
                                                    <span className="font-black text-emerald-600">{formatPV(p.cycle_stats?.cycle_left_pv)}</span>
                                                    <span className="text-gray-200">/</span>
                                                    <span className="font-black text-emerald-600">{formatPV(p.cycle_stats?.cycle_right_pv)}</span>
                                                </div>
                                                <span className="text-[9px] text-gray-400 font-bold uppercase truncate max-w-[80px]">{p.cycle_stats?.cycle_label}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex gap-2 items-center font-bold">
                                                <span className="text-blue-600">{formatPV(p.effective_left_pv)}</span>
                                                <span className="text-gray-200">|</span>
                                                <span className="text-purple-600">{formatPV(p.effective_right_pv)}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex gap-2 items-center text-[11px] font-bold text-gray-400">
                                                <span>L: {formatPV(p.left_carry_forward_pv)}</span>
                                                <span>R: {formatPV(p.right_carry_forward_pv)}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-black">{formatPV(p.matching_pv)} PV</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className={`font-black text-sm ${p.matching_income > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                                {formatCurrency(p.matching_income)}
                                            </p>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {p.eligibility_bonus_sent ? (
                                                <div className="flex items-center justify-center gap-1.5 text-green-500">
                                                    <CheckCircle size={16} />
                                                    <span className="text-[10px] font-black uppercase">Paid</span>
                                                </div>
                                            ) : p.is_eligible ? (
                                                <button
                                                    onClick={() => sendEligibilityBonus(p.user_identity_id)}
                                                    className="px-3 py-1.5 bg-yellow-400 hover:bg-yellow-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest shadow-md shadow-yellow-100 transition-all active:scale-95"
                                                >
                                                    Pay Bonus
                                                </button>
                                            ) : (
                                                <span className="text-[10px] font-bold text-gray-300 italic uppercase">Locked</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex gap-2 items-center">
                                                {p.payout_status === 'new' && (
                                                    <button
                                                        onClick={() => processSingleUser(p.user_identity_id)}
                                                        className="p-2 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-colors shadow-sm active:scale-95"
                                                        title="Process Payout"
                                                    >
                                                        <Play size={16} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => setUpdateModal(p)}
                                                    className="p-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-colors shadow-sm active:scale-95"
                                                    title="Modify PV/CF"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="lg:hidden divide-y divide-gray-100/50">
                        {filteredPayouts.map((p, idx) => (
                            <div key={idx} className={`p-5 transition-colors ${p.is_eligible && p.matching_income > 0 ? 'bg-green-50/20' : 'hover:bg-gray-50'}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-lg font-black shadow-lg ${p.is_eligible && p.matching_income > 0
                                            ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white'
                                            : p.is_eligible
                                                ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
                                                : 'bg-gray-100 text-gray-400'
                                            }`}>
                                            {(p.user_name || 'U').charAt(0)}
                                        </div>
                                        <div>
                                            <h4 className="font-black text-gray-900 leading-none mb-1">{p.user_name || 'N/A'}</h4>
                                            <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest italic">{p.referral_code}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        {p.payout_status === 'new' ? (
                                            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-lg text-[9px] font-black uppercase border border-blue-100">Live Cycle</span>
                                        ) : p.payout_status === 'complete' ? (
                                            <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded-lg text-[9px] font-black uppercase border border-green-100">Processed</span>
                                        ) : (
                                            <span className="px-2 py-0.5 bg-gray-50 text-gray-400 rounded-lg text-[9px] font-black uppercase border border-gray-100">Inactive</span>
                                        )}
                                        {p.is_eligible && <p className="text-[8px] font-black text-emerald-600 uppercase mt-1 tracking-tighter">✓ Eligible Partner</p>}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <div className="bg-gray-50/50 p-3 rounded-2xl border border-gray-100">
                                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-[0.1em] block mb-1.5">Cycle PV (L/R)</span>
                                        <div className="flex items-center gap-2 font-black text-emerald-600">
                                            <span>{formatPV(p.cycle_stats?.cycle_left_pv)}</span>
                                            <span className="text-gray-200">/</span>
                                            <span>{formatPV(p.cycle_stats?.cycle_right_pv)}</span>
                                        </div>
                                        <span className="text-[8px] font-bold text-gray-300 uppercase truncate mt-0.5 block">{p.cycle_stats?.cycle_label}</span>
                                    </div>
                                    <div className="bg-white p-3 rounded-2xl border border-indigo-50 shadow-sm shadow-indigo-50/30">
                                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-[0.1em] block mb-1.5">Effective (L/R)</span>
                                        <div className="flex items-center gap-2 font-black text-indigo-600">
                                            <span>{formatPV(p.effective_left_pv)}</span>
                                            <span className="text-gray-200">|</span>
                                            <span>{formatPV(p.effective_right_pv)}</span>
                                        </div>
                                    </div>
                                    <div className="bg-white p-3 rounded-2xl border border-gray-100">
                                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-[0.1em] block mb-1.5">Carry Fwd (L/R)</span>
                                        <div className="flex items-center gap-2 font-bold text-gray-500 text-xs">
                                            <span>{formatPV(p.left_carry_forward_pv)}</span>
                                            <span className="text-gray-200">/</span>
                                            <span>{formatPV(p.right_carry_forward_pv)}</span>
                                        </div>
                                    </div>
                                    <div className="bg-gray-900 p-3 rounded-2xl border border-gray-800 shadow-xl shadow-gray-200/50">
                                        <span className="text-[9px] font-black text-gray-500 uppercase tracking-[0.1em] block mb-1">Matching Result</span>
                                        <p className="text-sm font-black text-yellow-400">{formatPV(p.matching_pv)} <span className="text-[9px] text-gray-400">PV MATCH</span></p>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                                    <div className="flex gap-4">
                                        <div>
                                            <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-0.5">Cycle Income</span>
                                            <p className={`text-lg font-black ${p.matching_income > 0 ? 'text-green-600' : 'text-gray-400 italic'}`}>
                                                {formatCurrency(p.matching_income)}
                                            </p>
                                        </div>
                                        <div>
                                            <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">Bonus</span>
                                            {p.eligibility_bonus_sent ? (
                                                <div className="flex items-center gap-1 text-green-600">
                                                    <CheckCircle size={14} />
                                                    <span className="text-[10px] font-black uppercase italic">Paid</span>
                                                </div>
                                            ) : p.is_eligible ? (
                                                <button
                                                    onClick={() => sendEligibilityBonus(p.user_identity_id)}
                                                    className="px-2 py-1 bg-yellow-400 text-white rounded-lg text-[9px] font-black uppercase shadow-lg shadow-yellow-100"
                                                >
                                                    Pay Bonus
                                                </button>
                                            ) : (
                                                <span className="text-[9px] text-gray-300 font-black uppercase italic">Locked</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        {p.payout_status === 'new' && (
                                            <button
                                                onClick={() => processSingleUser(p.user_identity_id)}
                                                className="p-3 bg-indigo-600 text-white rounded-xl shadow-lg shadow-indigo-100 active:scale-95"
                                            >
                                                <Play size={18} />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setUpdateModal(p)}
                                            className="p-3 bg-white text-blue-600 rounded-xl shadow-sm border border-blue-50 active:scale-95"
                                        >
                                            <Edit size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {filteredPayouts.length === 0 && (
                        <div className="p-12 text-center">
                            <Users size={40} className="mx-auto text-gray-100 mb-2" />
                            <p className="text-sm font-bold text-gray-400 italic">No partners found matching criteria.</p>
                        </div>
                    )}
                </div>

                {/* Update PV & CF Modal */}
                {updateModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold text-gray-900">
                                    Update PV & CF: {updateModal.user_name}
                                </h3>
                                <button onClick={() => setUpdateModal(null)} className="text-gray-400 hover:text-gray-600">
                                    <X size={20} />
                                </button>
                            </div>

                            <form onSubmit={(e) => {
                                e.preventDefault();
                                const formData = new FormData(e.target);
                                handleUpdateValue(
                                    updateModal.user_identity_id,
                                    formData.get('left_pv'),
                                    formData.get('right_pv'),
                                    formData.get('left_cf'),
                                    formData.get('right_cf')
                                );
                            }}>
                                <div className="space-y-4 mb-6">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Left PV Baseline</label>
                                            <input
                                                type="number"
                                                name="left_pv"
                                                defaultValue={updateModal.left_pv || 0}
                                                step="0.01"
                                                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Right PV Baseline</label>
                                            <input
                                                type="number"
                                                name="right_pv"
                                                defaultValue={updateModal.right_pv || 0}
                                                step="0.01"
                                                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Left Carry Forward</label>
                                            <input
                                                type="number"
                                                name="left_cf"
                                                defaultValue={updateModal.left_carry_forward_pv || 0}
                                                step="0.01"
                                                className="w-full px-3 py-2 border border-blue-200 bg-blue-50 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Right Carry Forward</label>
                                            <input
                                                type="number"
                                                name="right_cf"
                                                defaultValue={updateModal.right_carry_forward_pv || 0}
                                                step="0.01"
                                                className="w-full px-3 py-2 border border-purple-200 bg-purple-50 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none"
                                            />
                                        </div>
                                    </div>

                                    <p className="text-[10px] text-gray-400 italic">
                                        * Updating PV Baseline affects how NEW PV is calculated. CF update directly changes the carry forward amounts.
                                    </p>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setUpdateModal(null)}
                                        className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-semibold"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-bold shadow-lg shadow-indigo-200"
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Confirmation Modal */}
                {confirmModal && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
                            {/* Modal Header */}
                            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white/20 rounded-lg">
                                        <AlertTriangle className="text-white" size={20} />
                                    </div>
                                    <h3 className="text-lg font-semibold text-white">{confirmModal.title}</h3>
                                </div>
                                <button
                                    onClick={() => setConfirmModal(null)}
                                    className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                                >
                                    <X className="text-white" size={20} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6">
                                <p className="text-gray-600 leading-relaxed">{confirmModal.message}</p>
                            </div>

                            {/* Modal Footer */}
                            <div className="px-6 pb-6 flex gap-3">
                                <button
                                    onClick={() => setConfirmModal(null)}
                                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmModal.onConfirm}
                                    className={`flex-1 px-4 py-3 text-white rounded-xl transition-colors font-medium ${confirmModal.confirmColor === 'red'
                                        ? 'bg-red-600 hover:bg-red-700'
                                        : 'bg-indigo-600 hover:bg-indigo-700'
                                        }`}
                                >
                                    {confirmModal.confirmText || 'Confirm'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default AdminBinaryPayouts;
